import { type JSX } from 'react';
import { useIntl } from 'react-intl';

import { HideIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../../../buttons/button/Button.js';
import { Flex } from '../../../../../layouts/flex/Flex.js';
import { globalSprinkles } from '../../../../../styles/safe-sprinkles.js';
import { Heading } from '../../../../../typography/heading/Heading.js';
import { Text } from '../../../../../typography/text/Text.js';
import { dataTableMessages } from '../../../messages.js';
import * as styles from './DataTableAllColumnsHiddenEmptyState.sty.js';

/**
 * Props for the empty state shown when all columns are hidden and hidden-column listing is disabled.
 * @internal
 */
interface DataTableAllColumnsHiddenEmptyStateProps {
  /**
   * Callback invoked when the user clicks the CTA to enable the "list hidden columns" switch.
   */
  onListHiddenColumns: () => void;
}

/**
 * Empty state shown in the column settings modal when all columns are hidden
 * and the "list hidden columns" switch is off.
 * @internal
 */
export function DataTableAllColumnsHiddenEmptyState({
  onListHiddenColumns,
}: Readonly<DataTableAllColumnsHiddenEmptyStateProps>): JSX.Element {
  const intl = useIntl();

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      flex={1}
      paddingY={24}
      paddingX={16}
      gap={8}
      className={styles.emptyState}
    >
      <HideIcon />
      <Heading level={6} as="h2">
        {intl.formatMessage(dataTableMessages.allColumnsHiddenTitle)}
      </Heading>
      <Text>
        {intl.formatMessage(dataTableMessages.allColumnsHiddenDescription)}
      </Text>
      <Button
        variant="emphasized"
        className={globalSprinkles({ marginTop: 8 })}
        onClick={onListHiddenColumns}
        data-dt-children-name="ColumnSettingsListHiddenColumnsAction"
      >
        {intl.formatMessage(dataTableMessages.listHiddenColumnsAction)}
      </Button>
    </Flex>
  );
}
